import { GetProgrammaticCampaignEngagementBreakdownRO } from "@intentsify/dto";
import { ProgrammaticCampaignEngagementBreakdownAttributionMethod } from "@intentsify/types";
import { isDefined } from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import { Card, MIN_MODAL_CONTENT_HEIGHT, PieChart } from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { useMemo, useState } from "react";
import { AiOutlineBarChart } from "react-icons/ai";
import { DisplayPerformanceFilters } from "../../../../types";
import { NOT_A_CPA_CAMPAIGN_MESSAGE } from "screens/CampaignPacing/screens/displayPerformance.utils";
import { ChartExport, useExportableChart } from "shared/components";

type ClickThroughProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  totalConversions: number;
  getEngagementBreakdown: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignEngagementBreakdownRO>;
};

const TITLE = "Click-Through Vs View-Through Conversions";

const ClickThrough = (props: ClickThroughProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <ClickThroughContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const ClickThroughContent = ({
  campaignId,
  filters,
  totalConversions,
  getEngagementBreakdown,
}: ClickThroughProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: engagementData, isFetching: isLoadingEngagementData } =
    useQuery(
      ["impressionsClicksPerformance-engagement", campaignId, filters],
      () => getEngagementBreakdown(campaignId, filters)
    );

  const sortedEngagementData = useMemo(() => {
    const mapped = (engagementData?.engagementBreakdown ?? []).map((i) => ({
      value: i.countConversions,
      label: i.attributionMethod,
    }));

    const order = [
      ProgrammaticCampaignEngagementBreakdownAttributionMethod.CLICK_THROUGH,
      ProgrammaticCampaignEngagementBreakdownAttributionMethod.VIEW_THROUGH,
      ProgrammaticCampaignEngagementBreakdownAttributionMethod.NOT_ATTRIBUTED,
    ];

    return order
      .map((i) => mapped.find((m) => m.label === i))
      .filter(isDefined);
  }, [engagementData?.engagementBreakdown]);

  const clickThroughChart = useExportableChart({
    title: TITLE,
    campaignId: Number(campaignId),

    chart: (
      <PieChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        isLoading={isLoadingEngagementData}
        legendPosition="bottom"
        data={sortedEngagementData}
        noDataMessage="No conversions to display."
      />
    ),
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      title={clickThroughChart.title}
      isExpandable
      actions={
        <ChartExport
          size="small"
          campaignId={Number(campaignId)}
          title={clickThroughChart.title}
          onExportPNG={clickThroughChart.downloadAsPng}
          data={(engagementData?.engagementBreakdown ?? []).map((i) => ({
            value: i.countConversions,
            label: i.attributionMethod,
          }))}
        />
      }
      hasData={totalConversions !== 0}
      noDataIcon={AiOutlineBarChart}
      noDataMessage={NOT_A_CPA_CAMPAIGN_MESSAGE}
      isLoading={isLoadingEngagementData}
    >
      {clickThroughChart.component}
      {clickThroughChart.exportComponent}
    </Card>
  );
};

export { ClickThrough };
